import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './BlogPost.css';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const blogPosts = {
    'understanding-database-normalization': {
      title: "Understanding Database Normalization: From 0NF to 3NF with Practical Examples",
      date: "2025-06-24",
      readTime: "4 min read",
      tags: ["Database", "Backend"],
      content: `
        <h2>Introduction</h2>
        <p>Database normalization is a fundamental concept in database design that helps organize data efficiently and eliminate redundancy. In this comprehensive guide, we'll explore the journey from an unnormalized table (0NF) to Third Normal Form (3NF) with practical examples.</p>

        <h2>What is Database Normalization?</h2>
        <p>Database normalization is the process of organizing data in a database to reduce redundancy and improve data integrity. It involves decomposing tables into smaller, related tables and defining relationships between them.</p>

        <h2>The Normal Forms</h2>

        <h3>0NF (Unnormalized Form)</h3>
        <p>This is where we start - a flat table with repeating groups and no structure. Data is often duplicated and difficult to maintain.</p>

        <h3>1NF (First Normal Form)</h3>
        <p>To achieve 1NF, we ensure that:</p>
        <ul>
          <li>Each column contains atomic (indivisible) values</li>
          <li>Each column contains values of a single data type</li>
          <li>Each column has a unique name</li>
          <li>The order of rows and columns doesn't matter</li>
        </ul>

        <h3>2NF (Second Normal Form)</h3>
        <p>To achieve 2NF, the table must:</p>
        <ul>
          <li>Be in 1NF</li>
          <li>Have no partial dependencies (non-key attributes must depend on the entire primary key)</li>
        </ul>

        <h3>3NF (Third Normal Form)</h3>
        <p>To achieve 3NF, the table must:</p>
        <ul>
          <li>Be in 2NF</li>
          <li>Have no transitive dependencies (non-key attributes should not depend on other non-key attributes)</li>
        </ul>

        <h2>Practical Example</h2>
        <p>Let's walk through a practical example of normalizing a student enrollment system from 0NF to 3NF.</p>

        <h3>0NF Example</h3>
        <p>Starting with an unnormalized table that contains student information, courses, and instructor details all in one place.</p>

        <h3>Applying Normalization</h3>
        <p>We'll systematically apply each normal form, showing how the data structure improves with each step, reducing redundancy and improving maintainability.</p>

        <h2>Benefits of Normalization</h2>
        <ul>
          <li>Reduced data redundancy</li>
          <li>Improved data integrity</li>
          <li>Easier maintenance and updates</li>
          <li>Better storage efficiency</li>
          <li>Reduced risk of data anomalies</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Understanding database normalization is crucial for designing efficient and maintainable database systems. By following these principles, you can create robust data structures that serve your applications well as they scale.</p>
      `
    }
  };

  const post = blogPosts[slug];

  if (!post) {
    return (
      <section className="blog-post-page section">
        <div className="container">
          <div className="post-not-found">
            <h2>Post Not Found</h2>
            <p>The blog post you're looking for doesn't exist.</p>
            <button onClick={() => navigate('/blog')} className="back-to-blog">
              Back to Blog
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="blog-post-page section">
      <div className="container">
        <motion.article
          className="blog-post"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <header className="blog-post__header">
            <button onClick={() => navigate('/blog')} className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Blog
            </button>

            <div className="blog-post__meta">
              <span className="blog-post__date">{post.date}</span>
              <span className="blog-post__read-time">{post.readTime}</span>
            </div>

            <h1 className="blog-post__title">{post.title}</h1>

            <div className="blog-post__tags">
              {post.tags.map((tag, index) => (
                <span key={index} className="blog-post__tag">{tag}</span>
              ))}
            </div>
          </header>

          <div
            className="blog-post__content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </motion.article>
      </div>
    </section>
  );
};

export default BlogPost;